import type { Meter } from "@/models/meter"
import type { Bill } from "@/services/core-api/types"
import { getDisplayValue } from "@/utils/value"

import type { GridSortModel } from "@mui/x-data-grid-pro"

import type { BillGridRecord } from "./bill.types"

export const defaultBillsSortModel = [
  { field: "start_date", sort: "desc" as const },
]

const gridFieldToApiSortField: Record<string, string> = {
  meter_code: "meter_id",
  site_name: "site_name",
}

const apiSortFieldToGridField: Record<string, string> = {
  meter_id: "meter_code",
}

const sortableGridFields = new Set([
  "cost",
  "end_date",
  "flow_direction",
  "meter_code",
  "resource_type",
  "site_name",
  "start_date",
  "usage",
])

export const billsGridFieldToSortParam = (
  field: string,
  sort: "asc" | "desc" | null | undefined
): string | undefined => {
  if (!sort) {
    return undefined
  }

  const apiField = gridFieldToApiSortField[field] ?? field
  return `${apiField}:${sort}`
}

export const billsSortParamToSortModel = (
  sortParam: string | null | undefined
): GridSortModel => {
  if (!sortParam) {
    return defaultBillsSortModel
  }

  const separatorIndex = sortParam.indexOf(":")
  if (separatorIndex <= 0) {
    return defaultBillsSortModel
  }

  const apiField = sortParam.slice(0, separatorIndex)
  const direction = sortParam.slice(separatorIndex + 1)
  if (direction !== "asc" && direction !== "desc") {
    return defaultBillsSortModel
  }

  const gridField = apiSortFieldToGridField[apiField] ?? apiField
  if (!sortableGridFields.has(gridField)) {
    return defaultBillsSortModel
  }

  return [{ field: gridField, sort: direction }]
}

export interface BillMeterLookup {
  meter_code?: string | null
  site_id?: string | null
  site_name?: string | null
}

export const getUniqueBillMeterIds = (
  pages: { items: Pick<Bill, "meter_id">[] }[] | undefined
): number[] => [
  ...new Set(
    pages?.flatMap((page) => page.items.map((bill) => bill.meter_id)) ?? []
  ),
]

export const buildMeterLookupById = (
  meters: Meter[] | undefined,
  isPlaceholderData: boolean
): Map<Meter["id"], BillMeterLookup> => {
  const meterLookupById = new Map<Meter["id"], BillMeterLookup>()

  if (isPlaceholderData) {
    return meterLookupById
  }

  meters?.forEach((meter) => {
    if (meter.id == null) {
      return
    }

    meterLookupById.set(meter.id, {
      meter_code: meter.meterCode,
      site_id: meter.site?.id ?? null,
      site_name: meter.site?.name,
    })
  })

  return meterLookupById
}

export const billToGridRecord = (
  bill: Bill,
  meterLookupById?: Map<Meter["id"], BillMeterLookup>
): BillGridRecord => {
  const lookup = meterLookupById?.get(bill.meter_id.toString())

  return {
    ...bill,
    meter_code: getDisplayValue(lookup?.meter_code),
    site_id: lookup?.site_id ?? null,
    site_name: getDisplayValue(lookup?.site_name),
  }
}
